import React, { useMemo } from 'react';
import { DatePicker, parseDate } from '@ark-ui/react/date-picker';
import { parseDateTime } from '@internationalized/date';
import type { CalendarDateTime } from '@internationalized/date';
import { Portal } from '../../../components/Portal';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface DateTimePickerRangeInputProps extends BaseInputProps<string[]> {
  /** Minimální volitelné datum (ISO `YYYY-MM-DD`). */
  min?: string;
  /** Maximální volitelné datum (ISO `YYYY-MM-DD`). */
  max?: string;
  /** Krok minut v nabídce (default 5). */
  minuteStep?: number;
  /** BCP-47 locale pro zobrazení data (RHF drží vždy ISO). */
  locale?: string;
  /** Počet zobrazených měsíců (default 2 — vhodné pro výběr rozsahu). */
  numOfMonths?: number;
  /** Lokalizované popisky. */
  triggerLabel?: string;
  fromLabel?: string;
  toLabel?: string;
  hourLabel?: string;
  minuteLabel?: string;
  className?: string;
}

const toDateValue = (iso: string | undefined | null) => {
  if (!iso) return undefined;
  try {
    return parseDate(iso);
  } catch {
    return undefined;
  }
};

const pad = (n: number) => String(n).padStart(2, '0');

interface Endpoint {
  dateISO: string;
  hour: number;
  minute: number;
}

const parseEndpoint = (iso: string | undefined): Endpoint => {
  if (!iso) return { dateISO: '', hour: 0, minute: 0 };
  const [datePart, timePart = '00:00'] = iso.split('T');
  const [h, m] = timePart.split(':');
  return { dateISO: datePart, hour: Number(h) || 0, minute: Number(m) || 0 };
};

/**
 * Atomický výběr rozsahu data a času (od–do) postavený na Ark UI `DatePicker`
 * v režimu `selectionMode="range"`, doplněném o výběr hodin/minut pro každý
 * konec rozsahu. Ark nemá time-picker, proto je čas řešen nativními `<select>`
 * v patičce kalendáře — jedna řada pro začátek, jedna pro konec.
 *
 * RHF drží hodnotu jako pole dvou ISO datetime řetězců `[fromISO, toISO]`
 * (`YYYY-MM-DDTHH:mm`, bez sekund). Během výběru může mít pole délku 1 (vybrán
 * jen začátek) — `onBlur` (touched) se hlásí až po dokončení rozsahu. Popover
 * zůstává otevřený i po výběru obou dat (`closeOnSelect={false}`), aby šel
 * doplnit čas.
 *
 * POZOR: ISO tvar bez sekund se NIKDY nepředává do `parseDate` (vyžaduje
 * sekundy a bez nich vyhodí výjimku) — vždy se rozdělí na datum + čas a skládá
 * ručně přes `parseDateTime` (s dosazenými `:00`).
 */
export const DateTimePickerRangeInput: React.FC<
  DateTimePickerRangeInputProps
> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  placeholder,
  describedBy,
  labelId,
  testAttributes,
  min,
  max,
  minuteStep = 5,
  locale = 'cs-CZ',
  numOfMonths = 2,
  triggerLabel = 'Otevřít kalendář',
  fromLabel = 'Od',
  toLabel = 'Do',
  hourLabel = 'Hodina',
  minuteLabel = 'Minuta',
  className,
}) => {
  // Dvojice koncových bodů (vždy délky 2, chybějící doplněné prázdné).
  const endpoints = useMemo<[Endpoint, Endpoint]>(
    () => [parseEndpoint(value?.[0]), parseEndpoint(value?.[1])],
    [value],
  );

  // Ark hodnota je pole `CalendarDateTime` — díky tomu nese čas, takže se
  // zobrazení v inputech obnoví i při změně času a výběr dne čas zachová.
  // Pole je poziční [start, end]: prázdný start vrací prázdné pole (range bez
  // začátku nedává smysl a kompaktace by konec posunula do start indexu, kde by
  // se zobrazil ve špatném inputu). Konec se přidá jen když je vyplněný.
  const dateValue = useMemo<CalendarDateTime[]>(() => {
    const toCDT = (ep: Endpoint) => {
      try {
        return parseDateTime(
          `${ep.dateISO}T${pad(ep.hour)}:${pad(ep.minute)}:00`,
        );
      } catch {
        return undefined;
      }
    };
    if (!endpoints[0].dateISO) return [];
    const start = toCDT(endpoints[0]);
    if (!start) return [];
    if (!endpoints[1].dateISO) return [start];
    const end = toCDT(endpoints[1]);
    return end ? [start, end] : [start];
  }, [endpoints]);

  // Krokové minuty + aktuálně uložené minuty obou konců (i mimo grid, např.
  // z legacy dat nebo nedělitelného `minuteStep`) — jinak by `<select>` neměl
  // odpovídající `<option>` a spadl by na první (00), čímž by se zobrazená
  // minuta tiše rozešla s uloženou hodnotou.
  const minuteOptions = useMemo(() => {
    const grid = Array.from(
      { length: Math.ceil(60 / minuteStep) },
      (_, i) => i * minuteStep,
    );
    const stored = endpoints
      .map((ep) => ep.minute)
      .filter((m) => m >= 0 && m < 60);
    return Array.from(new Set([...grid, ...stored])).sort((a, b) => a - b);
  }, [minuteStep, endpoints]);

  // Změna času jednoho konce: ostatní hodnoty zůstanou beze změny.
  const composeTime = (index: 0 | 1, h: number, m: number): string[] => {
    const ep = endpoints[index];
    if (!ep.dateISO) return value ?? [];
    const next = [...(value ?? [])];
    next[index] = `${ep.dateISO}T${pad(h)}:${pad(m)}`;
    return next;
  };

  // Vlastní zobrazení v inputu: lokalizované datum + čas za ním
  // („21. 1. 2026 09:36"). Čas se čte přímo z `CalendarDateTime`.
  const formatDateTime = (date: {
    year: number;
    month: number;
    day: number;
    hour?: number;
    minute?: number;
  }) =>
    `${new Intl.DateTimeFormat(locale).format(
      new Date(date.year, date.month - 1, date.day),
    )} ${pad(date.hour ?? 0)}:${pad(date.minute ?? 0)}`;

  const renderTimeRow = (index: 0 | 1, label: string, testKey: string) => {
    const ep = endpoints[index];
    const rowDisabled = disabled || readOnly || !ep.dateISO;
    return (
      <div className={styles.dateTimeRangeRow}>
        <span className={styles.dateTimeLabel} aria-hidden="true">
          {label}
        </span>
        <select
          className={styles.dateTimeSelect}
          aria-label={`${label} – ${hourLabel}`}
          disabled={rowDisabled}
          value={ep.hour}
          onChange={(e) =>
            onChange(composeTime(index, Number(e.target.value), ep.minute))
          }
          onBlur={onBlur}
          data-testid={`${testAttributes['data-testid']}-${testKey}-hour`}
        >
          {Array.from({ length: 24 }, (_, h) => (
            <option key={h} value={h}>
              {pad(h)}
            </option>
          ))}
        </select>
        <span aria-hidden="true">:</span>
        <select
          className={styles.dateTimeSelect}
          aria-label={`${label} – ${minuteLabel}`}
          disabled={rowDisabled}
          value={ep.minute}
          onChange={(e) =>
            onChange(composeTime(index, ep.hour, Number(e.target.value)))
          }
          onBlur={onBlur}
          data-testid={`${testAttributes['data-testid']}-${testKey}-minute`}
        >
          {minuteOptions.map((m) => (
            <option key={m} value={m}>
              {pad(m)}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <DatePicker.Root
      name={name}
      ids={{ input: (i) => (i === 0 ? id : `${id}-end`), label: () => labelId }}
      value={dateValue}
      min={toDateValue(min)}
      max={toDateValue(max)}
      locale={locale}
      selectionMode="range"
      numOfMonths={numOfMonths}
      disabled={disabled}
      readOnly={readOnly}
      format={formatDateTime}
      // Po výběru data(t) zůstává otevřeno, aby šel doplnit čas obou konců.
      closeOnSelect={false}
      onValueChange={(details) => {
        // Čas čteme přímo z vrácené hodnoty (`CalendarDateTime` ho nese přes
        // `preserveTime`), NE z poziční `endpoints[i]`: Ark v range režimu pole
        // přeskládá (když je konec dřív než začátek), takže poziční čtení by
        // časy obou konců prohodilo. Čerstvě vybraný `CalendarDate` čas nemá →
        // fallback 00:00.
        onChange(
          details.value.map((d) => {
            const dt = d as { hour?: number; minute?: number };
            return `${d.toString().slice(0, 10)}T${pad(dt.hour ?? 0)}:${pad(
              dt.minute ?? 0,
            )}`;
          }),
        );
        // Touched až po dokončení rozsahu (obě data vybraná).
        if (details.value.length >= 2) onBlur();
      }}
      positioning={{ placement: 'bottom-start', gutter: 4 }}
      lazyMount
      unmountOnExit
      className={clsx(styles.colorRoot, className)}
    >
      <DatePicker.Control className={styles.dateTimeRangeControl}>
        {/* Display-only: zobrazená hodnota nese datum i čas (přes `format`).
            Datum se vybírá kalendářem, čas selecty — proto jsou inputy readOnly,
            aby `fixOnBlur` nezkoušel parsovat zpět řetězec s časem. Inputy jsou
            naskládané pod sebou: datetime řetězec je delší než šířka pole, takže
            dvě hodnoty vedle sebe by se ořezávaly. Jeden trigger vedle dvojice. */}
        <div className={styles.dateTimeRangeInputs}>
          {/* FormField asociuje svůj `<label>` jen s prvním inputem (index 0),
              proto oba inputy dostávají explicitní `aria-label` (Od/Do) — jinak
              by druhý input neměl přístupný název (WCAG 4.1.2). */}
          <DatePicker.Input
            ref={ref}
            index={0}
            readOnly
            placeholder={placeholder}
            onBlur={onBlur}
            className={clsx(styles.dateInput, styles.dateTimeRangeInput)}
            aria-label={fromLabel}
            aria-describedby={describedBy}
            aria-invalid={hasError || undefined}
            {...testAttributes}
          />
          <DatePicker.Input
            index={1}
            readOnly
            placeholder={placeholder}
            onBlur={onBlur}
            className={clsx(styles.dateInput, styles.dateTimeRangeInput)}
            aria-label={toLabel}
            aria-describedby={describedBy}
            aria-invalid={hasError || undefined}
            data-testid={`${testAttributes['data-testid']}-end`}
          />
        </div>
        <DatePicker.Trigger
          className={clsx(styles.dateTrigger, styles.dateTimeRangeTrigger)}
          aria-label={triggerLabel}
        >
          <Icon name="calendar" size={18} />
        </DatePicker.Trigger>
      </DatePicker.Control>
      <Portal>
        <DatePicker.Positioner>
          <DatePicker.Content
            className={styles.dateContent}
            data-testid={`${testAttributes['data-testid']}-calendar`}
          >
            <DatePicker.View view="day">
              <DatePicker.Context>
                {(api) => (
                  <>
                    <DatePicker.ViewControl className={styles.dateViewControl}>
                      <DatePicker.PrevTrigger className={styles.datePrevTrigger}>
                        <Icon name="chevron-left" size={16} />
                      </DatePicker.PrevTrigger>
                      <DatePicker.RangeText className={styles.dateViewTrigger} />
                      <DatePicker.NextTrigger className={styles.dateNextTrigger}>
                        <Icon name="chevron-right" size={16} />
                      </DatePicker.NextTrigger>
                    </DatePicker.ViewControl>
                    <div className={styles.dateRangeMonths}>
                      {Array.from({ length: numOfMonths }, (_, offset) => {
                        const month = api.getOffset({ months: offset });
                        return (
                          <DatePicker.Table
                            key={offset}
                            className={styles.dateTable}
                          >
                            <DatePicker.TableHead>
                              <DatePicker.TableRow>
                                {api.weekDays.map((weekDay, index) => (
                                  <DatePicker.TableHeader
                                    key={index}
                                    className={styles.dateTableHeader}
                                  >
                                    {weekDay.narrow}
                                  </DatePicker.TableHeader>
                                ))}
                              </DatePicker.TableRow>
                            </DatePicker.TableHead>
                            <DatePicker.TableBody>
                              {month.weeks.map((week, weekIndex) => (
                                <DatePicker.TableRow key={weekIndex}>
                                  {week.map((day, dayIndex) => (
                                    <DatePicker.TableCell
                                      key={dayIndex}
                                      value={day}
                                      visibleRange={month.visibleRange}
                                    >
                                      <DatePicker.TableCellTrigger
                                        className={styles.dateCellTrigger}
                                      >
                                        {day.day}
                                      </DatePicker.TableCellTrigger>
                                    </DatePicker.TableCell>
                                  ))}
                                </DatePicker.TableRow>
                              ))}
                            </DatePicker.TableBody>
                          </DatePicker.Table>
                        );
                      })}
                    </div>
                    {/* Patička s výběrem času pro oba konce rozsahu — prosté
                        potomky Content, ne Ark part. */}
                    <div className={styles.dateTimeRangeFooter}>
                      {renderTimeRow(0, fromLabel, 'from')}
                      {renderTimeRow(1, toLabel, 'to')}
                    </div>
                  </>
                )}
              </DatePicker.Context>
            </DatePicker.View>
          </DatePicker.Content>
        </DatePicker.Positioner>
      </Portal>
    </DatePicker.Root>
  );
};

DateTimePickerRangeInput.displayName = 'DateTimePickerRangeInput';
